import React, { useMemo, useState } from 'react';
import { BuildingIcon, HomeIcon, ArrowRightIcon, TrendingUpIcon } from 'lucide-react';
import { motion } from 'framer-motion';
type PropertyType = 'PPOR' | 'Investment';
type LoanType = 'PI' | 'IO';
interface Loan {
  id: string;
  name: string;
  amount: number;
  rate: number;
  term: number;
  type: LoanType;
  securedAgainstId: string;
}
interface Renovation {
  enabled: boolean;
  cost: number;
  expectedUplift: number;
  rentIncrease: number;
}
interface Property {
  id: string;
  name: string;
  type: PropertyType;
  value: number;
  weeklyRent: number;
  annualExpenses: number;
  loans: Loan[];
  renovation: Renovation;
}
interface PortfolioVisualMapProps {
  properties: Property[];
  onNavigateToSetup?: () => void;
}
interface Connection {
  fromPropertyId: string;
  toPropertyId: string;
  loan: Loan;
  fromProperty: Property;
  toProperty: Property;
}
interface PropertyPosition {
  id: string;
  x: number;
  y: number;
}
const formatCurrency = (val: number) => new Intl.NumberFormat('en-AU', {
  style: 'currency',
  currency: 'AUD',
  maximumFractionDigits: 0
}).format(val);
export function PortfolioVisualMap({
  properties,
  onNavigateToSetup
}: PortfolioVisualMapProps) {
  const [hoveredPropertyId, setHoveredPropertyId] = useState<string | null>(null);
  const [hoveredConnection, setHoveredConnection] = useState<Connection | null>(null);
  const [tooltipPos, setTooltipPos] = useState({
    x: 0,
    y: 0
  });
  // Calculate connections (cross-property loans)
  const connections = useMemo(() => {
    const conns: Connection[] = [];
    properties.forEach(prop => {
      prop.loans.forEach(loan => {
        if (loan.securedAgainstId !== prop.id) {
          const fromProp = properties.find(p => p.id === loan.securedAgainstId);
          if (fromProp) {
            conns.push({
              fromPropertyId: loan.securedAgainstId,
              toPropertyId: prop.id,
              loan,
              fromProperty: fromProp,
              toProperty: prop
            });
          }
        }
      });
    });
    return conns;
  }, [properties]);
  // Calculate property positions
  const positions = useMemo((): PropertyPosition[] => {
    const pporProps = properties.filter(p => p.type === 'PPOR');
    const investProps = properties.filter(p => p.type === 'Investment');
    const positions: PropertyPosition[] = [];
    const cardHeight = 180;
    const verticalSpacing = 300;
    const leftX = 60;
    const rightX = 680;
    // Position PPORs on the left
    pporProps.forEach((prop, idx) => {
      positions.push({
        id: prop.id,
        x: leftX,
        y: 100 + idx * verticalSpacing
      });
    });
    // Position Investments on the right
    investProps.forEach((prop, idx) => {
      positions.push({
        id: prop.id,
        x: rightX,
        y: 100 + idx * verticalSpacing
      });
    });
    return positions;
  }, [properties]);
  const getPosition = (id: string) => positions.find(p => p.id === id);
  // Calculate total height for container
  const pporCount = properties.filter(p => p.type === 'PPOR').length;
  const investCount = properties.filter(p => p.type === 'Investment').length;
  const maxColumn = Math.max(pporCount, investCount);
  const containerHeight = Math.max(500, maxColumn * 300 + 140);
  if (properties.length === 0) {
    return <div className="flex flex-col items-center justify-center py-24 text-center" data-id="element-1293">
        <BuildingIcon className="w-16 h-16 text-slate-300 dark:text-slate-600 mb-4" data-id="element-1294" />
        <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2" data-id="element-1295">
          Add properties to visualise your portfolio structure
        </h3>
        <p className="text-slate-500 dark:text-slate-400 mb-6" data-id="element-1296">
          Head to the Properties & Loans tab to get started
        </p>
        {onNavigateToSetup && <button onClick={onNavigateToSetup} className="px-6 py-3 rounded-xl bg-gradient-to-r from-accent-500 to-primary-500 text-white font-semibold hover:shadow-[0_0_20px_rgba(20,184,166,0.4)] transition-all" data-id="element-1297">
            Go to Properties & Loans
          </button>}
      </div>;
  }
  return <div className="space-y-8" data-id="element-1298">
      {/* Visual Diagram */}
      <div className="bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-2xl p-8 overflow-x-auto" data-id="element-1299">
        <div className="relative" style={{
        minHeight: `${containerHeight}px`,
        minWidth: '1020px'
      }} data-id="element-1300">
          {/* SVG Overlay for connections */}
          <svg className="absolute inset-0 pointer-events-none" style={{
          width: '100%',
          height: '100%'
        }} data-id="element-1301">
            <defs data-id="element-1302">
              <marker id="arrowhead" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto" className="fill-slate-300 dark:fill-white/20" data-id="element-1303">
                <polygon points="0 0, 10 3, 0 6" data-id="element-1304" />
              </marker>
              <marker id="arrowhead-highlight" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto" className="fill-accent-500 dark:fill-accent-400" data-id="element-1305">
                <polygon points="0 0, 10 3, 0 6" data-id="element-1306" />
              </marker>
            </defs>

            {connections.map(conn => {
            const fromPos = getPosition(conn.fromPropertyId);
            const toPos = getPosition(conn.toPropertyId);
            if (!fromPos || !toPos) return null;
            const isHighlighted = hoveredPropertyId === conn.fromPropertyId || hoveredPropertyId === conn.toPropertyId || hoveredConnection?.loan.id === conn.loan.id;
            const x1 = fromPos.x + 280;
            const y1 = fromPos.y + 90;
            const x2 = toPos.x;
            const y2 = toPos.y + 90;
            const midX = (x1 + x2) / 2;
            const controlX1 = x1 + (midX - x1) * 0.7;
            const controlX2 = x2 - (x2 - midX) * 0.7;
            const pathD = `M ${x1} ${y1} C ${controlX1} ${y1}, ${controlX2} ${y2}, ${x2} ${y2}`;
            return <g key={conn.loan.id} data-id="element-1307">
                  <path d={pathD} stroke={isHighlighted ? '#10b981' : '#cbd5e1'} strokeWidth={isHighlighted ? 3 : 2} fill="none" markerEnd={isHighlighted ? 'url(#arrowhead-highlight)' : 'url(#arrowhead)'} className="transition-all duration-200 pointer-events-auto cursor-pointer dark:stroke-white/20" style={{
                strokeDasharray: isHighlighted ? 'none' : 'none'
              }} onMouseEnter={e => {
                setHoveredConnection(conn);
                setTooltipPos({
                  x: e.clientX,
                  y: e.clientY
                });
              }} onMouseMove={e => {
                setTooltipPos({
                  x: e.clientX,
                  y: e.clientY
                });
              }} onMouseLeave={() => setHoveredConnection(null)} data-id="element-1308" />
                  {/* Label on line */}
                  <foreignObject x={midX - 80} y={(y1 + y2) / 2 - 15} width="160" height="30" className="pointer-events-none" data-id="element-1309">
                    <div className="flex items-center justify-center" data-id="element-1310">
                      <div className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${isHighlighted ? 'bg-accent-500 text-white shadow-md' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-white/10'}`} data-id="element-1311">
                        {formatCurrency(conn.loan.amount)}
                      </div>
                    </div>
                  </foreignObject>
                </g>;
          })}
          </svg>

          {/* Property Cards */}
          {properties.map((prop, idx) => {
          const pos = getPosition(prop.id);
          if (!pos) return null;
          const totalLoans = prop.loans.reduce((sum, l) => sum + l.amount, 0);
          const effectiveValue = prop.renovation.enabled ? prop.value + prop.renovation.expectedUplift : prop.value;
          const equity = effectiveValue - totalLoans;
          const isHovered = hoveredPropertyId === prop.id;
          const isConnected = connections.some(c => c.fromPropertyId === prop.id || c.toPropertyId === prop.id);
          const shouldDim = hoveredPropertyId && !isHovered && hoveredPropertyId !== prop.id;
          const Icon = prop.type === 'PPOR' ? HomeIcon : BuildingIcon;
          const borderColor = prop.type === 'PPOR' ? 'border-primary-400' : 'border-accent-500';
          return <motion.div key={prop.id} initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: shouldDim ? 0.4 : 1,
            scale: isHovered ? 1.02 : 1
          }} transition={{
            delay: idx * 0.1,
            duration: 0.3
          }} className={`absolute w-[280px] bg-white dark:bg-white/5 border-2 border-slate-200 dark:border-white/10 ${borderColor} border-l-4 rounded-2xl shadow-sm hover:shadow-lg transition-all cursor-pointer`} style={{
            left: `${pos.x}px`,
            top: `${pos.y}px`
          }} onMouseEnter={() => setHoveredPropertyId(prop.id)} onMouseLeave={() => setHoveredPropertyId(null)} data-id="element-1312">
                <div className="p-5" data-id="element-1313">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4" data-id="element-1314">
                    <div className="flex items-center gap-3" data-id="element-1315">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${prop.type === 'PPOR' ? 'bg-primary-100 dark:bg-primary-500/20 text-primary-600 dark:text-primary-400' : 'bg-accent-100 dark:bg-accent-500/20 text-accent-600 dark:text-accent-400'}`} data-id="element-1316">
                        <Icon className="w-5 h-5" data-id="element-1317" />
                      </div>
                      <div data-id="element-1318">
                        <h4 className="font-semibold text-slate-900 dark:text-white text-sm leading-tight" data-id="element-1319">
                          {prop.name}
                        </h4>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider font-medium ${prop.type === 'PPOR' ? 'bg-primary-100 dark:bg-primary-500/20 text-primary-700 dark:text-primary-300' : 'bg-accent-100 dark:bg-accent-500/20 text-accent-700 dark:text-accent-300'}`} data-id="element-1320">
                          {prop.type}
                        </span>
                      </div>
                    </div>
                    {prop.loans.length > 0 && <div className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400" data-id="element-1321">
                        <span className="w-5 h-5 rounded-full bg-slate-100 dark:bg-white/10 flex items-center justify-center font-medium text-[10px]" data-id="element-1322">
                          {prop.loans.length}
                        </span>
                      </div>}
                  </div>

                  {/* Value */}
                  <div className="mb-3" data-id="element-1323">
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-1" data-id="element-1324">
                      Property Value
                    </p>
                    <p className="text-2xl font-bold text-slate-900 dark:text-white" data-id="element-1325">
                      {formatCurrency(effectiveValue)}
                    </p>
                  </div>

                  {/* Loans */}
                  {totalLoans > 0 && <div className="mb-3" data-id="element-1326">
                      <p className="text-xs text-slate-500 dark:text-slate-400 mb-1" data-id="element-1327">
                        Total Loans
                      </p>
                      <p className="text-lg font-semibold text-red-600 dark:text-red-400" data-id="element-1328">
                        {formatCurrency(totalLoans)}
                      </p>
                    </div>}

                  {/* Equity */}
                  <div className="pt-3 border-t border-slate-200 dark:border-white/10" data-id="element-1329">
                    <div className="flex items-center justify-between" data-id="element-1330">
                      <p className="text-xs text-slate-500 dark:text-slate-400" data-id="element-1331">
                        Equity
                      </p>
                      <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400" data-id="element-1332">
                        {formatCurrency(equity)}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>;
        })}
        </div>
      </div>

      {/* Tooltip */}
      {hoveredConnection && <div className="fixed z-50 bg-slate-800 text-white text-xs rounded-lg px-3 py-2 shadow-xl pointer-events-none" style={{
      left: `${tooltipPos.x + 10}px`,
      top: `${tooltipPos.y + 10}px`
    }} data-id="element-1333">
          <p className="font-semibold mb-1" data-id="element-1334">{hoveredConnection.loan.name}</p>
          <p data-id="element-1335">Amount: {formatCurrency(hoveredConnection.loan.amount)}</p>
          <p data-id="element-1336">Rate: {hoveredConnection.loan.rate}%</p>
          <p data-id="element-1337">
            Type:{' '}
            {hoveredConnection.loan.type === 'IO' ? 'Interest Only' : 'Principal & Interest'}
          </p>
          <p className="text-slate-300 mt-1 text-[10px]" data-id="element-1338">
            Secured against {hoveredConnection.fromProperty.name}
          </p>
        </div>}

      {/* Legend */}
      <div className="bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl p-4" data-id="element-1339">
        <h4 className="text-sm font-semibold text-slate-900 dark:text-white mb-3" data-id="element-1340">
          How to Read This Map
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs text-slate-600 dark:text-slate-400" data-id="element-1341">
          <div className="flex items-start gap-2" data-id="element-1342">
            <div className="w-4 h-4 rounded bg-primary-100 dark:bg-primary-500/20 flex-shrink-0 mt-0.5" data-id="element-1343"></div>
            <div data-id="element-1344">
              <p className="font-medium text-slate-900 dark:text-white" data-id="element-1345">
                PPOR Properties
              </p>
              <p data-id="element-1346">Your primary residence(s)</p>
            </div>
          </div>
          <div className="flex items-start gap-2" data-id="element-1347">
            <div className="w-4 h-4 rounded bg-accent-100 dark:bg-accent-500/20 flex-shrink-0 mt-0.5" data-id="element-1348"></div>
            <div data-id="element-1349">
              <p className="font-medium text-slate-900 dark:text-white" data-id="element-1350">
                Investment Properties
              </p>
              <p data-id="element-1351">Income-generating assets</p>
            </div>
          </div>
          <div className="flex items-start gap-2" data-id="element-1352">
            <ArrowRightIcon className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" data-id="element-1353" />
            <div data-id="element-1354">
              <p className="font-medium text-slate-900 dark:text-white" data-id="element-1355">
                Equity Flow
              </p>
              <p data-id="element-1356">Shows cross-property loan security</p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1357">
        {onNavigateToSetup && <button onClick={onNavigateToSetup} className="flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-slate-100 dark:bg-white/5 hover:bg-slate-200 dark:hover:bg-white/10 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white font-semibold transition-all" data-id="element-1358">
            <TrendingUpIcon className="w-5 h-5" data-id="element-1359" />
            See Full Breakdown
          </button>}
        <a href="#contact" className="flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-gradient-to-r from-accent-500 to-primary-500 text-white font-semibold hover:shadow-[0_0_20px_rgba(20,184,166,0.4)] transition-all" data-id="element-1360">
          <BuildingIcon className="w-5 h-5" data-id="element-1361" />
          Book a Strategy Session
        </a>
      </div>
    </div>;
}